import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Server, Monitor, HardDrive, ExternalLink } from 'lucide-react';
import { useAtivos } from '@/hooks/useAtivos';
import { useState } from 'react';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';

interface MudancaCIsImpactadosProps {
  cisImpactados?: any;
  onUpdate?: (cis: any) => void;
  readOnly?: boolean;
}

const MudancaCIsImpactados = ({ 
  cisImpactados = [], 
  onUpdate,
  readOnly = false 
}: MudancaCIsImpactadosProps) => {
  const ativosQuery = useAtivos();
  const ativos = ativosQuery.data || [];
  const [selectedAtivoId, setSelectedAtivoId] = useState<string>('');

  const handleAddCI = () => {
    if (selectedAtivoId && onUpdate) {
      const ativo = ativos.find(a => a.id === selectedAtivoId);
      if (ativo && !cisImpactados.find((ci: any) => ci.id === ativo.id)) {
        onUpdate([...cisImpactados, {
          id: ativo.id,
          nome: ativo.nome,
          tipo: ativo.tipo_ativo?.nome || ativo.tipo_id,
          status: ativo.status_operacional,
        }]);
        setSelectedAtivoId('');
      }
    }
  };

  const handleRemoveCI = (ciId: string) => {
    if (onUpdate) {
      onUpdate(cisImpactados.filter((ci: any) => ci.id !== ciId));
    }
  };

  const getIcon = (tipo: string) => {
    const tipoLower = tipo?.toLowerCase() || '';
    if (tipoLower.includes('servidor')) return <Server className="w-4 h-4" />;
    if (tipoLower.includes('monitor')) return <Monitor className="w-4 h-4" />;
    return <HardDrive className="w-4 h-4" />;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <HardDrive className="w-5 h-5" />
            CIs Impactados
          </span>
          <Badge variant="secondary">{cisImpactados.length} item(s)</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!readOnly && (
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="ativo">Adicionar Ativo</Label>
              <Select value={selectedAtivoId} onValueChange={setSelectedAtivoId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um ativo do CMDB" />
                </SelectTrigger>
                <SelectContent>
                  {ativos.map((ativo) => (
                    <SelectItem key={ativo.id} value={ativo.id}>
                      {ativo.nome} - {ativo.tipo_ativo?.nome || ativo.tipo_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={handleAddCI} 
              disabled={!selectedAtivoId}
              className="mt-auto"
            >
              Adicionar
            </Button>
          </div>
        )}

        <div className="space-y-2">
          {cisImpactados.map((ci: any) => (
            <div 
              key={ci.id} 
              className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  {getIcon(ci.tipo)}
                </div>
                <div>
                  <p className="font-medium">{ci.nome}</p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>{ci.tipo}</span>
                    {ci.status && (
                      <>
                        <span>•</span>
                        <Badge variant="outline" className="text-xs">
                          {ci.status}
                        </Badge>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => window.open(`/cmdb?ativo=${ci.id}`, '_blank')}
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
                {!readOnly && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveCI(ci.id)}
                  >
                    ×
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {cisImpactados.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            Nenhum CI impactado selecionado
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MudancaCIsImpactados;
